// OP-3605 — Razão (ESL1): o livro de lançamentos do módulo. Append-only —
// toda posição do sistema é consequência desta lista, e correção entra como
// lançamento novo, nunca como apagamento. Estornar é alçada de ESL10.
import { FC, useEffect, useMemo, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useEstoqueItensSolicitacao, useEstoqueLocais, useEstoqueMovimentos, useEstoqueUnidadesDoItem } from '../../../../hooks/estoque'
import { usePerfilEstoque } from '../../../../hooks/estoque/perfil'
import { estornarEstoqueMovimento, registrarEstoqueMovimento, urlEstoque } from '../../../../Service/estoque'
import { MovimentoRow, MovimentosFilters, TipoMovimento } from '../../../../types/estoque'
import {
  BD, Button, CaixaLista, Card, Field, Folha, FolhaOverlay, FolhaPega, FolhaTopo, Input, ListaCartoes, PageSubtitle, PageTitle, Select, SoCelular, SoDesktop, Spinner, TabelaWrap, Table,
} from '../../styles'
import { fmtDateTime } from '../../statusUi'
import { tratarErro } from '../erros'
import { CartaoLinha } from '../CartaoLinha'
import { plural } from '../formato'
import { RefLink } from '../RefLink'
import { CampoComLeitor } from '../CampoComLeitor'
import { Filtros } from '../Filtros'
import {
  FormAvulso, TIPOS_AVULSOS, TIPO_AVULSO_UI, TIPO_MOVIMENTO_LABEL, TipoAvulso, ehLancamentoDeEstorno,
  impedimentoAvulso, impedimentoEstorno, motivoEstornoValido, pecasDoLocal, talvezTenhaMovidoDepois,
} from './regras'
import { LOCAL_EXECUTION } from '../../../../utils/featureFlag'

const FILTERS_KEY = 'estoque.razao.filtros'
const TIPOS = Object.keys(TIPO_MOVIMENTO_LABEL) as TipoMovimento[]
const COLS = 7

const loadFilters = (): MovimentosFilters | null => {
  try { const raw = sessionStorage.getItem(FILTERS_KEY); return raw ? JSON.parse(raw) : null } catch { return null }
}

const AVISO_MOVEU = 'A peça tem lançamento mais recente: o estorno provavelmente será recusado.'

const BotaoEstornar: FC<{ moveu: boolean; disabled: boolean; onClick: () => void }> = ({ moveu, disabled, onClick }) => (
  <div>
    <Button $variant="secondary" disabled={disabled} onClick={onClick} title={moveu ? AVISO_MOVEU : undefined}>Estornar</Button>
    {moveu && <div style={{ fontSize: 11, color: '#92400E', marginTop: 4 }}>{AVISO_MOVEU}</div>}
  </div>
)

export const EstoqueRazao: FC = () => {
  const navigate = useNavigate()
  const { pode } = usePerfilEstoque()
  const podeEstornar = pode('ESTORNAR')
  const podeLancar = pode('LANCAR_MOVIMENTO')
  const inicial = useMemo<MovimentosFilters>(() => loadFilters() ?? { page: 1 }, [])
  const [draft, setDraft] = useState<MovimentosFilters>(inicial)
  const [applied, setApplied] = useState<MovimentosFilters>(inicial)
  const { rows, total, lastPage, isLoading, isError, isUnauthorized, mutate } = useEstoqueMovimentos(applied)
  // A página da navegação é a PEDIDA, não a devolvida: sem resposta o
  // adaptador diz `page: 1`, e uma falha na página 2 desabilitava "Anterior"
  // e anunciava "Página 1" — sem saída de volta.
  const pagina = Math.max(1, Number(applied.page) || 1)
  const { locais } = useEstoqueLocais()
  const { data: itens } = useEstoqueItensSolicitacao()
  const [estornando, setEstornando] = useState<MovimentoRow | null>(null)
  const [motivo, setMotivo] = useState('')
  const [busy, setBusy] = useState(false)
  const AVULSO_VAZIO: FormAvulso = { tipo: '', itemId: null, localId: null, unidadeId: null, quantidade: '', motivo: '', referenciaId: '' }
  const [lancando, setLancando] = useState(false)
  const [avulso, setAvulso] = useState<FormAvulso>(AVULSO_VAZIO)
  // Só as peças daquele item naquela praça: escolher fora dali é o caminho
  // curto para `UNIDADE_EM_OUTRO_LOCAL`.
  const pecasQ = useEstoqueUnidadesDoItem(avulso.tipo === 'EXTRAVIO' ? avulso.itemId : null, avulso.localId)

  useEffect(() => { try { sessionStorage.setItem(FILTERS_KEY, JSON.stringify(applied)) } catch { /* ignora */ } }, [applied])

  if (isUnauthorized && !LOCAL_EXECUTION) return <Navigate to="/auth" replace />

  const aplicar = () => {
    const proximo = { ...draft, page: 1 }
    setApplied(proximo)
    if (urlEstoque('getEstoqueMovimentos', proximo) === urlEstoque('getEstoqueMovimentos', applied)) mutate()
  }
  const limpar = () => {
    setDraft({ page: 1 })
    setApplied({ page: 1 })
  }
  const ativos = [applied.busca, applied.tipo, applied.itemId, applied.localId, applied.unidadeId, applied.de, applied.ate]
    .filter((v) => v != null && String(v) !== '').length
  const contagem = isLoading ? 'Carregando…' : plural(total, 'lançamento', 'lançamentos')

  const confirmarEstorno = async () => {
    if (!estornando) return
    setBusy(true)
    try {
      await estornarEstoqueMovimento(estornando.id, motivo.trim())
      toast.success('Estorno lançado')
      setEstornando(null); setMotivo('')
      await mutate()
    } catch (err) {
      const e = tratarErro(err)
      if (e.tipo === 'sessao' && !LOCAL_EXECUTION) { navigate('/auth'); return }
      toast.error(e.texto)
      if (e.tipo === 'estado') await mutate()
    } finally {
      setBusy(false)
    }
  }

  const itemDoAvulso = itens.find((i) => i.id === avulso.itemId)
  // A consulta traz a peça que ESTÁ na praça e também a que só tem base nela.
  // O lançamento declara a praça como ORIGEM, e origem tem de ser a posição
  // real — senão o servidor recusa com UNIDADE_EM_OUTRO_LOCAL.
  const pecasNaPraca = pecasDoLocal(pecasQ.data, avulso.localId)
  const impAvulso = impedimentoAvulso(avulso)

  const lancarAvulso = async () => {
    setBusy(true)
    try {
      await registrarEstoqueMovimento({
        tipo: avulso.tipo, itemId: avulso.itemId, localId: avulso.localId,
        unidadeId: avulso.tipo === 'EXTRAVIO' ? avulso.unidadeId : null,
        quantidade: avulso.tipo === 'CONSUMO' ? Number(avulso.quantidade.trim()) : null,
        motivo: avulso.motivo.trim(), referenciaId: avulso.referenciaId.trim() || null,
      })
      toast.success('Lançamento registrado no razão')
      setAvulso(AVULSO_VAZIO); setLancando(false)
      await mutate()
    } catch (err) {
      const e = tratarErro(err)
      if (e.tipo === 'sessao' && !LOCAL_EXECUTION) { navigate('/auth'); return }
      toast.error(e.texto)
    } finally {
      setBusy(false)
    }
  }

  const fecharEstorno = () => { setEstornando(null); setMotivo('') }

  /** O mesmo formulário nas duas larguras: cartão no desktop, folha no celular. */
  const formEstorno = () => (
    <>
      <div style={{ fontSize: 13, color: BD.grayText, marginBottom: 8 }}>
        O estorno não apaga: grava o lançamento inverso apontando para este, uma vez só. Se a peça andou depois deste lançamento, o servidor recusa — a correção então é um lançamento novo, a partir de onde ela está agora.
      </div>
      {estornando && talvezTenhaMovidoDepois(rows, estornando) && (
        <div style={{ fontSize: 13, color: '#92400E', marginBottom: 8 }}>
          Esta peça tem lançamento mais recente nesta página. O servidor provavelmente vai recusar com "a peça moveu depois".
        </div>
      )}
      <Field><label>Motivo</label><Input value={motivo} maxLength={400} onChange={(e) => setMotivo(e.target.value)} /></Field>
      <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
        <Button disabled={busy || !motivoEstornoValido(motivo)} onClick={confirmarEstorno}>Confirmar estorno</Button>
        <Button $variant="secondary" disabled={busy} onClick={fecharEstorno}>Fechar</Button>
      </div>
    </>
  )

  const cartao = (m: MovimentoRow) => {
    const imp = impedimentoEstorno(m)
    const moveu = !imp && talvezTenhaMovidoDepois(rows, m)
    return (
      <CartaoLinha
        key={m.id}
        numero={`#${m.id}`}
        tipo={{ bg: BD.grayBg, color: BD.black, label: TIPO_MOVIMENTO_LABEL[m.tipo] ?? m.tipo }}
        quando={fmtDateTime(m.criadoEm)}
        titulo={(
          <>
            {m.itemNome}
            {m.serial ? <span style={{ fontFamily: 'monospace', fontWeight: 400, fontSize: 12, color: BD.grayText }}> · {m.serial}</span> : null}
          </>
        )}
        movimento={(
          <>
            {m.origemNome ?? '—'} → {m.destinoNome ?? '—'} · {m.quantidade}
            {ehLancamentoDeEstorno(m) ? <div>estorno do #{m.movimentoEstornoId}</div> : null}
            {m.estornadoPorId != null ? <div>estornado pelo #{m.estornadoPorId}</div> : null}
          </>
        )}
        referencia={<RefLink tipo={m.referenciaTipo} codigo={m.referenciaId} id={m.referenciaRefId ?? null} documento={m.referenciaDocumento} />}
        nota={<>{m.usuarioNome}{m.motivo ? ` · ${m.motivo}` : ''}</>}
        acao={podeEstornar ? (imp
          ? <span style={{ fontSize: 12, color: BD.grayText, textAlign: 'right' }}>{imp}</span>
          : <BotaoEstornar moveu={moveu} disabled={busy} onClick={() => { setEstornando(m); setMotivo('') }} />) : undefined}
      />
    )
  }

  const linha = (m: MovimentoRow) => {
    const imp = impedimentoEstorno(m)
    const moveu = !imp && talvezTenhaMovidoDepois(rows, m)
    return (
      <tr key={m.id}>
        <td style={{ whiteSpace: 'nowrap', fontSize: 13 }}>{fmtDateTime(m.criadoEm)}<div style={{ fontSize: 11, color: BD.grayText }}>#{m.id}</div></td>
        <td>
          {TIPO_MOVIMENTO_LABEL[m.tipo] ?? m.tipo}
          {ehLancamentoDeEstorno(m) && <div style={{ fontSize: 11, color: BD.grayText }}>estorno do #{m.movimentoEstornoId}</div>}
          {m.estornadoPorId != null && <div style={{ fontSize: 11, color: BD.grayText }}>estornado pelo #{m.estornadoPorId}</div>}
        </td>
        <td>
          {m.itemNome}
          {m.serial && (
            <div style={{ fontSize: 12 }}>
              <a href={`/interno/estoque/equipamentos/${m.unidadeId}`} style={{ fontFamily: 'monospace' }}
                 onClick={(e) => { e.preventDefault(); navigate(`/interno/estoque/equipamentos/${m.unidadeId}`) }}>{m.serial}</a>
            </div>
          )}
        </td>
        <td style={{ textAlign: 'right' }}>{m.quantidade}</td>
        <td style={{ fontSize: 13 }}>{m.origemNome ?? '—'} → {m.destinoNome ?? '—'}</td>
        <td style={{ fontSize: 13 }}>
          <RefLink tipo={m.referenciaTipo} codigo={m.referenciaId} id={m.referenciaRefId ?? null} documento={m.referenciaDocumento} />
          <div style={{ fontSize: 11, color: BD.grayText }}>{m.usuarioNome}{m.motivo ? ` · ${m.motivo}` : ''}</div>
        </td>
        <td>
          {podeEstornar && (imp
            ? <span style={{ fontSize: 12, color: BD.grayText }}>{imp}</span>
            : <BotaoEstornar moveu={moveu} disabled={busy} onClick={() => { setEstornando(m); setMotivo('') }} />)}
        </td>
      </tr>
    )
  }

  return (
    <>
      <PageTitle>Razão</PageTitle>
      <PageSubtitle>
        Todo lançamento do módulo, do mais recente ao mais antigo. O razão é append-only: nada se apaga, e corrigir é lançar o inverso.
        Posição, saldo e custódia são consequência desta lista.
      </PageSubtitle>

      <Filtros aoAplicar={aplicar} aoLimpar={limpar} ativos={ativos} resumo={contagem} confirmar="Aplicar filtros">
        <Field>
          <label>Referência, motivo ou serial</label>
          <CampoComLeitor valor={draft.busca ?? ''} aoMudar={(v) => setDraft((d) => ({ ...d, busca: v }))} tituloLeitor="Ler o serial" rotulo="Referência, motivo ou serial" aoConfirmar={aplicar} />
        </Field>
        <Field>
          <label>Tipo</label>
          <Select value={draft.tipo ?? ''} onChange={(e) => setDraft((d) => ({ ...d, tipo: e.target.value as TipoMovimento | '' }))}>
            <option value="">todos</option>
            {TIPOS.map((t) => <option key={t} value={t}>{TIPO_MOVIMENTO_LABEL[t]}</option>)}
          </Select>
        </Field>
        <Field>
          <label>Item</label>
          <Select value={draft.itemId ?? ''} onChange={(e) => setDraft((d) => ({ ...d, itemId: e.target.value }))}>
            <option value="">todos</option>
            {itens.map((i) => <option key={i.id} value={i.id}>{i.nome}</option>)}
          </Select>
        </Field>
        <Field>
          <label>Praça</label>
          <Select value={draft.localId ?? ''} onChange={(e) => setDraft((d) => ({ ...d, localId: e.target.value }))}>
            <option value="">todas</option>
            {locais.filter((l) => l.noEscopo).map((l) => <option key={l.id} value={l.id}>{l.nome}</option>)}
          </Select>
        </Field>
        <Field><label>De</label><Input type="date" value={draft.de ?? ''} onChange={(e) => setDraft((d) => ({ ...d, de: e.target.value }))} /></Field>
        <Field><label>Até</label><Input type="date" value={draft.ate ?? ''} onChange={(e) => setDraft((d) => ({ ...d, ate: e.target.value }))} /></Field>
      </Filtros>

      {podeLancar && (
        <Card style={{ padding: 16, marginBottom: 16 }}>
          {!lancando ? (
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <Button $variant="secondary" onClick={() => setLancando(true)}>Lançar movimento avulso</Button>
              <span style={{ fontSize: 12, color: BD.grayText }}>
                Só consumo e extravio nascem aqui. Separação, entrega, transferência, ajuste e baixa têm cada um o seu fluxo e a sua alçada.
              </span>
            </div>
          ) : (
            <>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Lançar movimento avulso</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                <Field>
                  <label>Tipo</label>
                  <Select value={avulso.tipo} onChange={(e) => setAvulso({ ...AVULSO_VAZIO, tipo: e.target.value as TipoAvulso | '' })}>
                    <option value="">— escolha —</option>
                    {TIPOS_AVULSOS.map((t) => <option key={t} value={t}>{TIPO_AVULSO_UI[t].rotulo}</option>)}
                  </Select>
                  {avulso.tipo && <div style={{ fontSize: 12, color: BD.grayText, marginTop: 4 }}>{TIPO_AVULSO_UI[avulso.tipo].explica}</div>}
                </Field>
                {avulso.tipo && (
                  <>
                    <Field>
                      <label>Item</label>
                      <Select value={avulso.itemId ?? ''} onChange={(e) => setAvulso({ ...avulso, itemId: Number(e.target.value) || null, unidadeId: null })}>
                        <option value="">— escolha —</option>
                        {itens.filter((i) => i.controle === TIPO_AVULSO_UI[avulso.tipo as TipoAvulso].regime)
                          .map((i) => <option key={i.id} value={i.id}>{i.nome}</option>)}
                      </Select>
                    </Field>
                    <Field>
                      <label>Praça</label>
                      <Select value={avulso.localId ?? ''} onChange={(e) => setAvulso({ ...avulso, localId: Number(e.target.value) || null, unidadeId: null })}>
                        <option value="">— escolha —</option>
                        {locais.filter((l) => l.noEscopo).map((l) => <option key={l.id} value={l.id}>{l.nome}</option>)}
                      </Select>
                    </Field>
                  </>
                )}
                {avulso.tipo === 'CONSUMO' && (
                  <Field>
                    <label>Quantidade{itemDoAvulso?.unidadeMedida ? ` (${itemDoAvulso.unidadeMedida})` : ''}</label>
                    <Input type="number" min={1} value={avulso.quantidade} onChange={(e) => setAvulso({ ...avulso, quantidade: e.target.value })} />
                  </Field>
                )}
                {avulso.tipo === 'EXTRAVIO' && avulso.itemId && avulso.localId && (
                  <Field>
                    <label>Peça</label>
                    {pecasQ.isError ? (
                      // Falha de consulta não é ausência de peça: dizer "não há
                      // nenhuma" seria transformar erro técnico em fato físico.
                      <div style={{ fontSize: 13, color: BD.red }}>
                        Não foi possível carregar as peças desta praça.{' '}
                        <Button $variant="secondary" onClick={() => pecasQ.mutate()}>Tentar de novo</Button>
                      </div>
                    ) : (
                      <>
                        <Select value={avulso.unidadeId ?? ''} onChange={(e) => setAvulso({ ...avulso, unidadeId: Number(e.target.value) || null })}>
                          <option value="">{pecasQ.isLoading ? 'carregando…' : pecasNaPraca.length ? '— escolha —' : 'nenhuma peça nesta praça'}</option>
                          {pecasNaPraca.map((u) => <option key={u.id} value={u.id}>{u.serial}</option>)}
                        </Select>
                        {!pecasQ.isLoading && pecasNaPraca.length === 0 && (
                          <div style={{ fontSize: 12, color: BD.grayText, marginTop: 4 }}>
                            Nenhuma peça deste item está NESTA praça agora. Peça com base aqui mas que saiu (com cliente, em trânsito ou no fornecedor) não se extravia daqui.
                          </div>
                        )}
                      </>
                    )}
                  </Field>
                )}
                {avulso.tipo && (
                  <>
                    <Field>
                      <label>Referência{avulso.tipo === 'CONSUMO' ? '' : ' (opcional)'}</label>
                      <Input value={avulso.referenciaId} maxLength={40} placeholder="documento ou evento que explica"
                             onChange={(e) => setAvulso({ ...avulso, referenciaId: e.target.value })} />
                    </Field>
                    <Field>
                      <label>Motivo</label>
                      <Input value={avulso.motivo} maxLength={400} onChange={(e) => setAvulso({ ...avulso, motivo: e.target.value })} />
                    </Field>
                  </>
                )}
              </div>
              <div style={{ display: 'flex', gap: 12, marginTop: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button disabled={busy || !!impAvulso} onClick={lancarAvulso}>Lançar</Button>
                <Button $variant="secondary" disabled={busy} onClick={() => { setAvulso(AVULSO_VAZIO); setLancando(false) }}>Descartar</Button>
                {impAvulso && <span style={{ fontSize: 13, color: BD.grayText }}>{impAvulso}</span>}
              </div>
            </>
          )}
        </Card>
      )}

      {estornando && (
        <SoDesktop>
          <Card style={{ padding: 16, marginBottom: 16 }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Estornar o lançamento #{estornando.id}</div>
            {formEstorno()}
          </Card>
        </SoDesktop>
      )}

      {estornando && (
        <SoCelular>
          <FolhaOverlay $open onClick={fecharEstorno} />
          <Folha $open role="dialog" aria-label="Estornar lançamento">
            <FolhaPega />
            <FolhaTopo>
              <h2>Estornar o #{estornando.id}</h2>
            </FolhaTopo>
            <div style={{ padding: 12, background: BD.grayBg, border: `1px solid ${BD.grayBorder}`, borderRadius: 10, fontSize: 13 }}>
              <b>{TIPO_MOVIMENTO_LABEL[estornando.tipo] ?? estornando.tipo}</b> · {estornando.itemNome}
              {estornando.serial ? <span style={{ fontFamily: 'monospace' }}> · {estornando.serial}</span> : ''}
              <div style={{ color: BD.grayText }}>
                {estornando.origemNome ?? '—'} → {estornando.destinoNome ?? '—'} · {fmtDateTime(estornando.criadoEm)}
              </div>
            </div>
            <div style={{ marginTop: 12 }}>{formEstorno()}</div>
          </Folha>
        </SoCelular>
      )}

      <CaixaLista>
        <div style={{ padding: '8px 12px', fontSize: 13, color: BD.grayText }}>{isLoading ? 'Carregando…' : isError ? '—' : plural(total, 'lançamento', 'lançamentos')}</div>
        <TabelaWrap>
          <Table>
            <thead><tr><th>Quando</th><th>Tipo</th><th>Item / peça</th><th style={{ textAlign: 'right' }}>Qtd.</th><th>Origem → destino</th><th>Referência</th><th /></tr></thead>
            <tbody>
              {isLoading && <tr><td colSpan={COLS} style={{ textAlign: 'center', padding: 24 }}><Spinner /></td></tr>}
              {!isLoading && isError && (
                <tr><td colSpan={COLS} style={{ textAlign: 'center', padding: 24, color: BD.red }}>
                  Não foi possível carregar o razão. <Button $variant="secondary" onClick={() => mutate()}>Tentar de novo</Button>
                </td></tr>
              )}
              {!isLoading && !isError && rows.length === 0 && <tr><td colSpan={COLS} style={{ textAlign: 'center', padding: 24, color: BD.grayText }}>Nenhum lançamento com os filtros atuais.</td></tr>}
              {!isLoading && !isError && rows.map(linha)}
            </tbody>
          </Table>
        </TabelaWrap>

        <ListaCartoes>
          {isLoading && <Card style={{ textAlign: 'center', padding: 24 }}><Spinner /></Card>}
          {!isLoading && isError && (
            <Card style={{ padding: 20, color: BD.red }}>
              Não foi possível carregar o razão. <Button $variant="secondary" onClick={() => mutate()}>Tentar de novo</Button>
            </Card>
          )}
          {!isLoading && !isError && rows.length === 0 && <Card style={{ padding: 20, color: BD.grayText }}>Nenhum lançamento com os filtros atuais.</Card>}
          {!isLoading && !isError && rows.map(cartao)}
        </ListaCartoes>
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, padding: 12 }}>
          <Button $variant="secondary" disabled={pagina <= 1} onClick={() => setApplied((a) => ({ ...a, page: pagina - 1 }))}>‹ Anterior</Button>
          <span style={{ fontSize: 13, color: BD.grayText }}>Página {pagina}</span>
          <Button $variant="secondary" disabled={!!isError || isLoading || !!lastPage} onClick={() => setApplied((a) => ({ ...a, page: pagina + 1 }))}>Próxima ›</Button>
        </div>
      </CaixaLista>
      {!podeEstornar && <div style={{ fontSize: 12, color: BD.grayText, marginTop: 8 }}>Estornar lançamento exige a permissão "Estornar movimento".</div>}
    </>
  )
}

export default EstoqueRazao
